import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AlertListService } from '@xref/ngx';

import { CountryPhoneCodesService } from '../../../../services/country-phone-codes/country-phone-codes.service';
import { ElementHelperService } from '../../../../services/element-helper/element-helper.service';
import { IdService } from '../../../../services/id/id.service';
import { SignupMfaLockoutService } from '../../../../services/signup-mfa-lockout/signup-mfa-lockout.service';
import { TokenService } from '../../../../services/token/token.service';
import { CHALLENGE_TYPE, TwoFactorService } from '../../../../services/two-factor/two-factor.service';

const RESEND_COOLDOWN_SECONDS = 60;
const MOBILE_OTP_INCORRECT = 'MOBILE_OTP_INCORRECT';

// Enterprise (XSP) setup stages: pick a method, then verify SMS OTP or app TOTP.
type EnterpriseStage = 'choose' | 'sms' | 'app';

@Component({
    selector: 'xlog-two-factor-setup',
    templateUrl: './two-factor-setup.component.pug',
    styleUrls: ['./two-factor-setup.component.scss'],
    standalone: false
})
export class TwoFactorSetupComponent implements OnInit, OnDestroy {

    @ViewChild('selectTypeForm') selectTypeForm: any;
    @ViewChild('setupSmsForm') setupSmsForm: any;
    @ViewChild('verifyForm') verifyForm: any;

    @Output() isSetupUpdated = new EventEmitter();
    @Input() isSetup: boolean;

    submitting = false;
    submitted = false;

    selected: boolean;

    // Enterprise (XSP) setup state.
    readonly CHALLENGE_TYPE = CHALLENGE_TYPE;
    stage: EnterpriseStage = 'choose';
    method = '';
    provisioningUri = '';
    verifyModel = { mfa_code: '' };
    codeError = false;
    resendCountdown = 0;
    private resendTimer: any = null;

    constructor(
        private token: TokenService,
        public twoFactor: TwoFactorService,
        private alertList: AlertListService,
        private el: ElementHelperService,
        public countryCodes: CountryPhoneCodesService,
        private id: IdService,
        private signupMfaLockout: SignupMfaLockoutService,
    ) {}

    ngOnInit() {
        if (this.twoFactor.isEnterprise()) {
            this.initEnterpriseDefaults();
            return;
        }

        if (!this.twoFactor.data.user_choice || this.twoFactor.data.user_choice.length === 0) {
            if (this.twoFactor.data.email_allowed) {
                this.twoFactor.data.user_choice = 'email';
            } else if (this.twoFactor.data.mobile_allowed) {
                this.twoFactor.data.user_choice = 'sms';
            } else {
                this.twoFactor.data.user_choice = '';
            }
        }
    }

    ngOnDestroy() {
        this.clearResendTimer();
    }

    // ---------------------------------------------------------------------------
    // Enterprise (XSP) setup
    // ---------------------------------------------------------------------------

    /**
     * Default the method choice to an actually-accepted method: SMS when a
     * verified mobile is available, otherwise the authenticator app. Left empty
     * when neither is accepted so the Continue button stays disabled and we
     * never advance into a flow the backend hasn't offered.
     */
    initEnterpriseDefaults() {
        if (this.twoFactor.data.mfa_sms_accepted) {
            this.method = CHALLENGE_TYPE.SMS;
        } else if (this.twoFactor.data.mfa_app_accepted) {
            this.method = CHALLENGE_TYPE.APP;
        } else {
            this.method = '';
        }
    }

    /**
     * Advance from the method-choice screen: dispatch the SMS OTP, or fetch the
     * authenticator-app provisioning URI used to render the QR code.
     */
    continueEnterpriseSetup() {
        this.submitting = true;
        this.codeError = false;

        if (this.method === CHALLENGE_TYPE.SMS) {
            this.twoFactor.requestXspSmsSetupCode(this.token.data.token).subscribe(
                () => {
                    this.stage = 'sms';
                    this.resetCodeEntry();
                    this.startResendCountdown();
                    this.submitting = false;
                },
                (err: any) => this.handleSetupRequestError(err)
            );
        } else {
            this.twoFactor.requestXspAppProvisioningUri(this.token.data.token).subscribe(
                (res: any) => {
                    this.provisioningUri = res.data.provisioning_uri;
                    this.stage = 'app';
                    this.resetCodeEntry();
                    this.submitting = false;
                },
                (err: any) => this.handleSetupRequestError(err)
            );
        }
    }

    /**
     * Verify the entered code for the chosen Enterprise method. On success
     * id.xref.io mints login tokens and the user is redirected.
     */
    verifyEnterpriseSetup() {
        this.submitted = true;
        this.codeError = false;

        if (this.verifyForm && this.verifyForm.invalid) return;

        this.submitting = true;

        const success = (res: any) => {
            this.clearResendTimer();
            this.id.completeMfaLogin(res.data);
        };

        const error = (err: any) => this.handleVerifyError(err);

        if (this.stage === 'sms') {
            this.twoFactor.verifyXspSmsSetupCode(this.token.data.token, this.verifyModel).subscribe(success, error);
        } else {
            this.twoFactor.verifyXspAppSetupCode(this.token.data.token, this.verifyModel).subscribe(success, error);
        }
    }

    /**
     * Re-send the SMS OTP during setup, respecting the resend cooldown.
     */
    resendEnterpriseSmsCode() {
        if (this.resendCountdown > 0 || this.submitting) return;

        this.submitting = true;
        this.codeError = false;

        this.twoFactor.requestXspSmsSetupCode(this.token.data.token).subscribe(
            () => {
                this.alertList.append([{active: true, type: 'success', messageKey: 'twoFactor.alerts.codeResent'}]);
                this.startResendCountdown();
                this.submitting = false;
            },
            (err: any) => this.handleSetupRequestError(err)
        );
    }

    /**
     * Return to the method-choice screen from an SMS/app verification screen.
     */
    backToMethodChoice() {
        this.clearResendTimer();
        this.stage = 'choose';
        this.resetCodeEntry();
    }

    // ---------------------------------------------------------------------------
    // Legacy (Recruiter/Admin) setup
    // ---------------------------------------------------------------------------

    /**
   * Select 2FA type. If email is selected submit instantly since no setup is required.
   */
    selectType() {
        if (this.twoFactor.data.user_choice === 'email') {
            this.submit();
        } else {
            this.selected = true;
            this.el.focusById('phoneCountry');
        }
    }

    /**
   * Set default selection of initial form component.
   */
    unselectType() {
        this.twoFactor.data.user_choice = 'email';
        this.selected = false;
    }

    /**
   * Store selected country code in sms model
   */
    selectCountry() {
        if (this.twoFactor.smsModel.country_code.length > 0)
            this.el.focusById('phone');
    }

    /**
   * Submit initial 2FA setup.
   */
    submit() {
        this.submitted = true;

        if (this.setupSmsForm && this.setupSmsForm.invalid) return;

        this.submitting = true;

        const success = (res: any) => {
            this.twoFactor.data.obfuscated_destination = res.data.obfuscated_destination;

            this.isSetup = true;
            this.isSetupUpdated.next(this.isSetup);
        };

        const error = (err: any) => {
            this.submitting = false;

            if (err.error.hasOwnProperty('phone_number')) {
                this.alertList.append([{active: true, type: 'danger', messageKey: 'twoFactor.alerts.invalidPhoneNumber'}]);
            } else {
                this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error'}]);
            }
        };

        if (this.twoFactor.data.user_choice === 'sms') {
            this.twoFactor
                .requestSmsVerificationCode(this.token.data.token, this.twoFactor.smsModel)
                .subscribe(success, error);
        } else if (this.twoFactor.data.user_choice === 'email') {
            this.twoFactor
                .requestEmailVerificationCode(this.token.data.token)
                .subscribe(success, error);
        }
    }

    // ---------------------------------------------------------------------------
    // Shared helpers
    // ---------------------------------------------------------------------------

    private resetCodeEntry() {
        this.verifyModel.mfa_code = '';
        this.submitted = false;
        this.codeError = false;
    }

    private handleSetupRequestError(err: any) {
        this.submitting = false;
        if (err && err.error && err.error.hasOwnProperty('twilio_code')) {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'twoFactor.alerts.smsSendFailed'}]);
        } else {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error'}]);
        }
    }

    private handleVerifyError(err: any) {
        this.submitting = false;
        if (this.signupMfaLockout.handleIfSignupMfaExhausted(err)) {
            return;
        }
        if (err && err.error && (err.error.hasOwnProperty('mfa_code') || err.error.hasOwnProperty('otp_code'))) {
            this.codeError = true;
        } else if (this.getOtpErrorCode(err) === MOBILE_OTP_INCORRECT) {
            this.codeError = true;
        } else {
            this.alertList.append([{active: true, type: 'danger', messageKey: 'alerts.generic.error'}]);
        }
    }

    private getOtpErrorCode(err: any): string | null {
        if (!err || !err.error || !err.error.data || !err.error.data.details) {
            return null;
        }
        return err.error.data.details.code || null;
    }

    private startResendCountdown() {
        this.clearResendTimer();
        this.resendCountdown = RESEND_COOLDOWN_SECONDS;
        this.resendTimer = setInterval(() => {
            this.resendCountdown -= 1;
            if (this.resendCountdown <= 0) {
                this.clearResendTimer();
            }
        }, 1000);
    }

    private clearResendTimer() {
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = null;
        }
        this.resendCountdown = 0;
    }
}
